import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useDeleteStopMutation } from 'tripPlanner/hooks/api/chat'
import type { DrawerHotel } from '../components/HotelListingDrawer'
import type { DrawerExperience } from '../components/ExperienceListingDrawer/ExperienceDetailView'
import TripPlanDetailDrawer from '../components/TripPlanDetailDrawer'
import PlaceInfoDrawer from '../components/PlaceInfoDrawer'
import LocationInfoDrawer from '../components/LocationInfoDrawer'
import ChatOfferDetailDrawer from '../components/ChatOfferDetailDrawer'
import ChatExperienceDetailDrawer from '../components/ChatExperienceDetailDrawer'
import { useDrawerState } from '../hooks/useDrawerState'
import { useTripPlannerEventBus, useEventSubscription } from '../contexts/TripPlannerEventBus'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useItineraryContext } from '../contexts/ItineraryContext'

function DrawerContainer() {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBus()
  const { sendMessage } = useChatActions()
  const { itineraryData, refetchCurrentChatItinerary } = useItineraryContext()

  const {
    isDetailPanelOpen,
    selectedPlaceDetails,
    handleDetailPanelDismiss,
    handleDetailPanelAfterClose,
    isPlaceInfoDrawerOpen,
    selectedInlinePlace,
    handlePlaceInfoDrawerClose,
    handlePlaceInfoDrawerAfterClose,
    isLocationInfoDrawerOpen,
    selectedLocationInfo,
    handleLocationInfoDrawerClose,
    handleLocationInfoDrawerAfterClose,
    detailPanel,
    placeInfo,
    locationInfo,
    offerDrawer,
    resetDrawers,
  } = useDrawerState()

  const deleteStopMutation = useDeleteStopMutation()

  const handleDeleteStopFromDrawer = useCallback((stopId: number) => {
    deleteStopMutation.mutate(
      { stopId },
      {
        onSuccess: () => {
          showSnackbar(intl.formatMessage({
            id: 'bTOBsXcsZanMrwJG-9_W',
            defaultMessage: 'Stop deleted successfully',
            description: 'Success snackbar message after deleting a stop from the trip itinerary',
          }), 'success')
          refetchCurrentChatItinerary()
        },
        onError: () => {
          showSnackbar(intl.formatMessage({
            id: 'eiuITzmc4eW9taNKmjvj',
            defaultMessage: 'Failed to delete stop. Please try again.',
            description: 'Error snackbar message when stop deletion fails in the trip itinerary',
          }), 'critical')
        },
      },
    )
  }, [deleteStopMutation, refetchCurrentChatItinerary, intl])

  const handleOfferDrawerClose = useCallback(() => {
    offerDrawer.close()
  }, [offerDrawer])

  const handleOfferShowMap = useCallback((hotel: DrawerHotel) => {
    handleOfferDrawerClose()
    if (!hotel.geoLocation) return
    eventBus.dispatch({
      type: 'show_hotel_on_map',
      data: {
        searchQuery: hotel.location ?? hotel.name,
        offers: [{ id: hotel.id, name: hotel.name, coordinates: hotel.geoLocation }],
      },
    })
  }, [handleOfferDrawerClose, eventBus])

  const handleExperienceDrawerShowMap = useCallback((experience: DrawerExperience) => {
    handleOfferDrawerClose()
    if (!experience.geoLocation) return
    eventBus.dispatch({
      type: 'show_experience_on_map',
      data: {
        searchQuery: experience.name,
        offers: [{ id: experience.offerId, name: experience.name, coordinates: experience.geoLocation }],
      },
    })
  }, [handleOfferDrawerClose, eventBus])

  // ── Event bus subscriptions ──

  useEventSubscription('open_place_drawer', (event) => {
    placeInfo.open({ googlePlaceId: event.googlePlaceId, placeName: event.placeName })
  })

  useEventSubscription('open_location_drawer', (event) => {
    locationInfo.open(event.data)
  })

  useEventSubscription('open_detail_drawer', (event) => {
    detailPanel.open({
      googlePlaceId: event.googlePlaceId,
      placeName: event.placeName,
      countryName: event.countryName,
      stopId: event.stopId,
    })
  })

  useEventSubscription('open_offer_drawer', (event) => {
    offerDrawer.open({ offerId: event.offerId, searchParams: event.searchParams, offerType: event.offerType })
  })

  useEventSubscription('reset_chat_state', () => {
    resetDrawers()
  })

  return (
    <>
      {isDetailPanelOpen && selectedPlaceDetails && (
        <TripPlanDetailDrawer
          isOpen={isDetailPanelOpen}
          onDismiss={handleDetailPanelDismiss}
          onAfterClose={handleDetailPanelAfterClose}
          googlePlaceId={selectedPlaceDetails.googlePlaceId}
          placeName={selectedPlaceDetails.placeName}
          countryName={selectedPlaceDetails.countryName}
          stopId={selectedPlaceDetails.stopId}
          onDeleteStop={selectedPlaceDetails.stopId !== undefined ? handleDeleteStopFromDrawer : undefined}
        />
      )}
      {isPlaceInfoDrawerOpen && selectedInlinePlace && (
        <PlaceInfoDrawer
          isOpen={isPlaceInfoDrawerOpen}
          onClose={handlePlaceInfoDrawerClose}
          onAfterClose={handlePlaceInfoDrawerAfterClose}
          googlePlaceId={selectedInlinePlace.googlePlaceId}
          placeName={selectedInlinePlace.placeName}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
        />
      )}
      {isLocationInfoDrawerOpen && selectedLocationInfo && (
        <LocationInfoDrawer
          isOpen={isLocationInfoDrawerOpen}
          onClose={handleLocationInfoDrawerClose}
          onAfterClose={handleLocationInfoDrawerAfterClose}
          locationData={selectedLocationInfo}
          category={selectedLocationInfo.category}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
        />
      )}
      {offerDrawer.data?.offerType === 'experience' && (
        <ChatExperienceDetailDrawer
          offerId={offerDrawer.data.offerId}
          isOpen={offerDrawer.isOpen}
          onClose={handleOfferDrawerClose}
          onAfterClose={offerDrawer.afterClose}
          onShowMap={handleExperienceDrawerShowMap}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
        />
      )}
      {offerDrawer.data && offerDrawer.data.offerType !== 'experience' && (
        <ChatOfferDetailDrawer
          offerId={offerDrawer.data.offerId}
          isOpen={offerDrawer.isOpen}
          onClose={handleOfferDrawerClose}
          onAfterClose={offerDrawer.afterClose}
          onShowMap={handleOfferShowMap}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          searchParams={offerDrawer.data.searchParams}
        />
      )}
    </>
  )
}

export default DrawerContainer
